import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ArticleInput, PublicArticle } from "@vedaunion/shared";
import { apiFetch, ApiError } from "./api";

export interface ArticleListParams {
  status?: string;
  category?: string;
  featured?: boolean;
  limit?: number;
  all?: boolean;
  mine?: boolean;
}

const LIST_KEY = (params: ArticleListParams) => ["articles", "list", params] as const;
const DETAIL_KEY = (slug: string) => ["articles", "detail", slug] as const;

function buildQuery(params: ArticleListParams): string {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.category) sp.set("category", params.category);
  if (params.featured) sp.set("featured", "true");
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.all) sp.set("all", "true");
  if (params.mine) sp.set("mine", "true");
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export function useArticles(params: ArticleListParams = {}) {
  return useQuery<{ articles: PublicArticle[] }>({
    queryKey: LIST_KEY(params),
    queryFn: ({ signal }) =>
      apiFetch<{ articles: PublicArticle[] }>(`/api/articles${buildQuery(params)}`, { signal }),
  });
}

export function useArticle(slug: string | undefined) {
  return useQuery<{ article: PublicArticle }>({
    queryKey: DETAIL_KEY(slug ?? ""),
    queryFn: ({ signal }) =>
      apiFetch<{ article: PublicArticle }>(`/api/articles/${slug}`, { signal }),
    enabled: !!slug,
  });
}

export function useCreateArticle() {
  const qc = useQueryClient();
  return useMutation<{ article: PublicArticle }, ApiError, ArticleInput>({
    mutationFn: (body) => apiFetch<{ article: PublicArticle }>("/api/articles", { method: "POST", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}

export function useUpdateArticle(id: number) {
  const qc = useQueryClient();
  return useMutation<{ article: PublicArticle }, ApiError, Partial<ArticleInput>>({
    mutationFn: (body) =>
      apiFetch<{ article: PublicArticle }>(`/api/articles/${id}`, { method: "PATCH", body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}

export function useDeleteArticle() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; deleted: number }, ApiError, number>({
    mutationFn: (id) => apiFetch<{ ok: true; deleted: number }>(`/api/articles/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}

/** Duplicate an article — server creates a new draft copy with a fresh slug. */
export function useDuplicateArticle() {
  const qc = useQueryClient();
  return useMutation<{ article: PublicArticle }, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<{ article: PublicArticle }>(`/api/articles/${id}/duplicate`, { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articles"] }),
  });
}

export interface ArticleRevisionRef {
  id: number;
  versionNumber: number;
  title: string;
  note: string | null;
  editorId: number | null;
  createdAt: string;
}

export function useRevisions(id: number | null) {
  return useQuery<{ revisions: ArticleRevisionRef[] }>({
    queryKey: ["articles", "revisions", id],
    queryFn: ({ signal }) =>
      apiFetch<{ revisions: ArticleRevisionRef[] }>(`/api/articles/${id}/revisions`, { signal }),
    enabled: id !== null && id > 0,
  });
}

export interface ArticleRevisionFull {
  id: number;
  versionNumber: number;
  title: string;
  contentJson: unknown;
  contentHtml: string;
  note: string | null;
  createdAt: string;
}

export function useRevision(articleId: number | null, versionNumber: number | null) {
  return useQuery<{ revision: ArticleRevisionFull }>({
    queryKey: ["articles", "revision", articleId, versionNumber],
    queryFn: ({ signal }) =>
      apiFetch<{ revision: ArticleRevisionFull }>(
        `/api/articles/${articleId}/revisions/${versionNumber}`,
        { signal },
      ),
    enabled: articleId !== null && versionNumber !== null,
  });
}

export interface TagSuggestion {
  tag: string;
  count: number;
}

export function useTagSuggestions() {
  return useQuery<{ tags: TagSuggestion[] }>({
    queryKey: ["articles", "tags"],
    queryFn: ({ signal }) => apiFetch<{ tags: TagSuggestion[] }>(`/api/articles/tags`, { signal }),
    staleTime: 60_000,
  });
}
